import { useEffect, useState } from 'react';

import { Badge, Button, Card, Group, MultiSelect, Select, Stack, Text } from '@mantine/core';

import { ConversationAttachment } from '@/types/attachment';
import { EnquiryConvertResponse } from '@/types/enquiry';
import {
  EnquiryConvertCandidateWithAttachments,
  EnquiryExtractConvertRequest,
} from '@/types/extraction';
import { ConfidenceLevel, ExtractedCandidate } from '@/types/report-request';

interface ExtractionReviewPanelProps {
  candidates: ExtractedCandidate[];
  /** ReportConfigs for the enquiry's team. */
  configOptions: { value: string; label: string }[];
  configsLoading: boolean;
  /** Conversation files offered as per-candidate attachments. */
  files: ConversationAttachment[];
  teamId: string | null;
  /** When the enquiry is already triaged, confirm appends instead of converts. */
  alreadyTriaged: boolean;
  isSubmitting: boolean;
  onConfirm: (
    body: EnquiryExtractConvertRequest,
    append: boolean
  ) => Promise<EnquiryConvertResponse | undefined>;
  onCancel?: () => void;
  /** Opens the manual create-report-request form — the fallback when
   * extraction found nothing useful (or missed a request). */
  onCreateManually?: () => void;
  /** Inline (workspace) mode: hide the panel's own action row — the
   * intake strip owns the primary. */
  hideActions?: boolean;
  /** Inline mode: hands the strip this panel's submit routine plus the
   * count of candidates with a config picked (candidates without one
   * are skipped — the strip labels itself "Create N requests"). */
  registerSubmit?: (
    submit: (() => Promise<EnquiryConvertResponse | undefined>) | null,
    chosenCount: number
  ) => void;
}

function confidenceColor(value: ConfidenceLevel | undefined | null): string {
  switch (value) {
    case 'high':
      return 'green';
    case 'medium':
      return 'yellow';
    case 'low':
      return 'red';
    default:
      return 'gray';
  }
}

interface RowState {
  configId: string | null;
  attachmentIds: string[];
}

/**
 * Review panel for candidates returned from an extraction over the source
 * set. Per candidate the analyst picks a ReportConfig and optionally chooses
 * conversation files to attach (defaulting to the candidate's source files,
 * but freely changeable — provenance and attachments are distinct). Confirm
 * creates RRs, carrying both the provenance (from `candidate.sources`) and
 * the chosen attachments.
 */
export function ExtractionReviewPanel({
  candidates,
  configOptions,
  configsLoading,
  files,
  teamId,
  alreadyTriaged,
  isSubmitting,
  onConfirm,
  onCancel,
  onCreateManually,
  hideActions = false,
  registerSubmit,
}: Readonly<ExtractionReviewPanelProps>) {
  // Default each candidate's attachment selection to the files it was
  // extracted from (provenance) — a convenience the analyst can override.
  const [rows, setRows] = useState<Record<number, RowState>>({});
  // Re-seed whenever the candidate set is replaced (a fresh extraction run)
  // — rows are index-keyed, so stale picks would slide onto new candidates.
  useEffect(() => {
    const init: Record<number, RowState> = {};
    candidates.forEach((c, i) => {
      const sourceFileIds = (c.sources ?? [])
        .map((s) => s.conversation_attachment_id)
        .filter((id): id is string => Boolean(id));
      init[i] = { configId: c.report_config_id ?? null, attachmentIds: sourceFileIds };
    });
    setRows(init);
  }, [candidates]);

  const fileOptions = files.map((f) => ({ value: f.id, label: f.filename }));

  const update = (idx: number, patch: Partial<RowState>) =>
    setRows((prev) => ({ ...prev, [idx]: { ...prev[idx], ...patch } }));

  const buildChosen = (): EnquiryConvertCandidateWithAttachments[] => {
    const chosen: EnquiryConvertCandidateWithAttachments[] = [];
    candidates.forEach((c, i) => {
      const row = rows[i];
      if (!row?.configId) {
        return; // skip candidates with no config picked
      }
      chosen.push({
        candidate_index: -1, // ad-hoc extraction — no stored candidate list
        report_config_id: row.configId,
        subjects: c.subjects ?? null,
        jurisdictions: c.jurisdictions ?? null,
        expedited: c.expedited ?? false,
        attachment_ids: row.attachmentIds,
        sources: c.sources ?? [],
      });
    });
    return chosen;
  };

  const submit = async () => {
    const chosen = buildChosen();
    if (chosen.length === 0) {
      return undefined;
    }
    return onConfirm({ candidates: chosen, team_id: teamId }, alreadyTriaged);
  };

  // Inline mode: publish the submit routine + chosen count so the intake
  // strip's primary can drive this panel. Re-registered on every row
  // change so the strip always sees the latest picks.
  const chosenCount = candidates.filter((_, i) => rows[i]?.configId).length;
  useEffect(() => {
    if (!registerSubmit) {
      return;
    }
    registerSubmit(submit, chosenCount);
    return () => registerSubmit(null, 0);
  }, [rows, candidates, teamId, alreadyTriaged]);

  return (
    <Stack gap="md">
      {teamId === null && (
        <Text size="sm" c="orange">
          Assign a team to the enquiry before creating report requests — ReportConfigs are filtered
          per team.
        </Text>
      )}
      {candidates.length === 0 && (
        <Stack gap="xs">
          <Text c="dimmed" size="sm">
            No candidates were extracted — there may not be enough context in the selected sources
            (e.g. an email that just says “see attached”).
          </Text>
          {onCreateManually && (
            <Text size="sm">You can still create the report request manually.</Text>
          )}
        </Stack>
      )}
      {candidates.map((c, idx) => {
        const provenance = (c.sources ?? [])
          .map((s) => {
            if (s.conversation_attachment_id) {
              const f = files.find((x) => x.id === s.conversation_attachment_id);
              return f ? `file: ${f.filename}` : 'file';
            }
            return 'message';
          })
          .filter((v, i, a) => a.indexOf(v) === i);
        return (
          <Card key={idx} withBorder padding="sm" radius="sm">
            <Group gap="xs" mb={4}>
              <Badge color={confidenceColor(c.confidence)} variant="light" size="sm">
                {c.confidence ?? 'unknown'}
              </Badge>
              {c.report_config_slug && (
                <Badge color="blue" variant="light" size="sm">
                  {c.report_config_slug}
                </Badge>
              )}
              {c.expedited && (
                <Badge color="red" variant="light" size="sm">
                  expedited
                </Badge>
              )}
            </Group>
            <Text size="sm" fw={500}>
              Subjects: {(c.subjects ?? []).join(', ') || '(none)'}
            </Text>
            {c.reasoning && (
              <Text size="xs" c="dimmed" mb={4}>
                {c.reasoning}
              </Text>
            )}
            {provenance.length > 0 && (
              <Text size="xs" c="dimmed" mb="xs">
                Extracted from: {provenance.join(', ')}
              </Text>
            )}
            <Stack gap="xs">
              <Select
                label="Report config"
                placeholder="Skip this candidate"
                data={configOptions}
                value={rows[idx]?.configId ?? null}
                onChange={(value) => update(idx, { configId: value })}
                searchable
                clearable
                disabled={configsLoading}
              />
              <Group gap="xs" align="flex-end" wrap="nowrap">
                <MultiSelect
                  style={{ flex: 1 }}
                  label="Attach documents"
                  placeholder="Files to attach to this report request"
                  data={fileOptions}
                  value={rows[idx]?.attachmentIds ?? []}
                  onChange={(value) => update(idx, { attachmentIds: value })}
                  searchable
                  clearable
                />
                <Button
                  size="xs"
                  variant="subtle"
                  data-testid={`attach-docs-select-all-${idx}`}
                  disabled={
                    fileOptions.length === 0 ||
                    (rows[idx]?.attachmentIds ?? []).length === fileOptions.length
                  }
                  onClick={() => update(idx, { attachmentIds: fileOptions.map((o) => o.value) })}
                >
                  Attach all
                </Button>
              </Group>
            </Stack>
          </Card>
        );
      })}
      {!hideActions && (
        <Group justify="space-between">
          {onCreateManually ? (
            <Button
              variant="subtle"
              onClick={onCreateManually}
              disabled={isSubmitting}
              data-testid="review-create-manually"
            >
              Create manually instead
            </Button>
          ) : (
            <span />
          )}
          <Group justify="flex-end">
            {onCancel && (
              <Button variant="default" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </Button>
            )}
            <Button
              onClick={submit}
              loading={isSubmitting}
              disabled={!teamId || candidates.length === 0}
            >
              {alreadyTriaged ? 'Add report requests' : 'Create report requests'}
            </Button>
          </Group>
        </Group>
      )}
    </Stack>
  );
}
